import { MobileBottomNav } from "./MobileBottomNav"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, CheckCircle, TrendingUp, MessageCircle, Shield, Star } from "lucide-react"
import { motion } from "framer-motion"

const benefits = [
  {
    icon: TrendingUp,
    title: "Professional Marketing",
    desc: "Get your villa showcased to the right audience with professional photography guidance and SEO-optimized listings.",
  },
  {
    icon: MessageCircle,
    title: "Direct Guest Communication",
    desc: "Guests contact you directly and book with zero hidden charges. No middleman fees.",
  },
  {
    icon: Shield,
    title: "Free Listing",
    desc: "List your villa for FREE. Promotion services are optional and paid only when you choose.",
  },
  {
    icon: Star,
    title: "Local Network",
    desc: "Increased revenue potential through our growing local network of travelers across Maharashtra.",
  },
]

const steps = [
  { num: "1", text: "Fill out the application form" },
  { num: "2", text: "Our team reviews your property" },
  { num: "3", text: "Get listed and start receiving bookings" },
]

function ForOwnerPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-bold ml-3">For Villa Owners</h1>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
        <div className="relative container mx-auto px-6 py-16 md:py-24 text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            List Your Villa With Us
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-300 text-lg max-w-xl mx-auto"
          >
            Partner with VillaKosh to showcase your property to travelers seeking premium stays across Maharashtra.
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <h3 className="text-2xl font-bold text-center mb-10">Why List With Us?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 * i }}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center mb-4">
                <b.icon className="h-5 w-5 text-white" />
              </div>
              <h4 className="font-semibold text-lg mb-2">{b.title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h3 className="text-2xl font-bold text-center mb-10">How It Works</h3>
          <div className="space-y-6">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.15 * i }}
                className="flex items-center gap-4"
              >
                <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                  {s.num}
                </div>
                <p className="text-gray-800 font-medium">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-12 md:py-16 text-center">
        <div className="bg-black rounded-3xl p-8 md:p-12 max-w-lg mx-auto">
          <h3 className="text-2xl font-bold text-white mb-3">Ready to Get Started?</h3>
          <p className="text-gray-300 text-sm mb-8">
            Fill out our quick application form and our team will get back to you within 2 hours.
          </p>
          <a
            href="https://forms.gle/vjCS2cdc8M9ntU488"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-black font-semibold py-3 px-8 rounded-full hover:bg-gray-100 transition transform hover:scale-105"
          >
            Apply to List Your Villa
            <ArrowLeft className="h-4 w-4 rotate-180" />
          </a>
          <p className="mt-6 text-xs text-gray-400">Average response time: 2 hours</p>
        </div>
      </section>

      {/* Mobile Bottom Nav */}
      <div className="block md:hidden fixed bottom-0 left-0 right-0 z-50">
        <MobileBottomNav />
      </div>
    </div>
  )
}

export default ForOwnerPage
